import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./PolicyPaymentWorkspace.css";

function PolicyPaymentWorkspace() {
  const { policyId } = useParams();
  const [data, setData] = useState(null);
  const [tenders, setTenders] = useState([
    { payment_method: "", gross_tender_amount: "", reference: "" },
  ]);
  const [allocations, setAllocations] = useState({});
  const [quote, setQuote] = useState(null);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const response = await axiosInstance.get(`policy-payments/${policyId}/`);
    setData(response.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not open the payment window."));
  }, [policyId]);

  const updateTender = (index, field, value) => {
    setTenders((current) =>
      current.map((line, row) =>
        row === index ? { ...line, [field]: value } : line
      )
    );
    setQuote(null);
  };

  const calculate = async () => {
    try {
      const usable = tenders.filter(
        (line) => line.payment_method && Number(line.gross_tender_amount) > 0
      );
      const response = await axiosInstance.post(
        `policy-payments/${policyId}/quote/`,
        { tenders: usable }
      );
      setQuote(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Conversion failed.");
    }
  };

  const allocationTotal = useMemo(
    () =>
      Object.values(allocations).reduce(
        (sum, value) => sum + Number(value || 0),
        0
      ),
    [allocations]
  );

  const autoAllocate = () => {
    let remaining = Number(quote?.total_home_amount || 0);
    const next = {};
    (data?.bills || []).forEach((bill) => {
      const amount = Math.min(Number(bill.balance), remaining);
      if (amount > 0) next[bill.id] = amount.toFixed(2);
      remaining -= amount;
    });
    setAllocations(next);
  };

  const post = async () => {
    setSaving(true);
    try {
      const usableTenders = tenders.filter(
        (line) => line.payment_method && Number(line.gross_tender_amount) > 0
      );
      const allocationLines = Object.entries(allocations)
        .filter(([, amount]) => Number(amount) > 0)
        .map(([bill, amount]) => ({ bill, amount }));

      const response = await axiosInstance.post(
        `policy-payments/${policyId}/post/`,
        {
          payment_date: paymentDate,
          reference,
          tenders: usableTenders,
          allocations: allocationLines,
        }
      );
      toast.success(`Payment ${response.data.receipt} posted successfully.`);
      window.location.assign(`/policy-maintenance?policy=${policyId}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Payment posting failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="payment-loading">Opening payment window…</div>;

  return (
    <section className="policy-payment-page">
      <header className="payment-title">
        <div>
          <span>POLICY PAYMENT PROCESSING</span>
          <h1>{data.policy.code}</h1>
          <p>
            {data.policy.policyholder} · {data.policy.plan} ·{" "}
            {data.policy.status}
          </p>
        </div>
        <button
          className="secondary"
          onClick={() =>
            window.location.assign(
              `/policy-maintenance?policy=${policyId}`
            )
          }
        >
          Back to maintenance
        </button>
      </header>

      <div className="payment-policy-summary">
        <article><span>Policy balance</span><strong>{data.policy.balance}</strong></article>
        <article><span>Payment mode</span><strong>{data.policy.payment_mode}</strong></article>
        <article><span>Open bills</span><strong>{data.bills.length}</strong></article>
        <article><span>Payment date</span><input type="date" value={paymentDate} onChange={(e)=>setPaymentDate(e.target.value)}/></article>
      </div>

      <section className="payment-panel">
        <header>
          <div><span>1</span><h2>Tenders and currency conversion</h2></div>
          <button className="secondary" onClick={() => setTenders([...tenders,{payment_method:"",gross_tender_amount:"",reference:""}])}>Add tender</button>
        </header>
        <div className="tender-table">
          <table>
            <thead><tr><th>Payment method</th><th>Gross tender</th><th>Reference</th><th>Handling</th><th>Rate</th><th>Home amount</th><th></th></tr></thead>
            <tbody>
              {tenders.map((line,index)=>{
                const result=quote?.tenders?.[index];
                return <tr key={index}>
                  <td><select value={line.payment_method} onChange={(e)=>updateTender(index,"payment_method",e.target.value)}><option value="">Select</option>{data.payment_methods.map((method)=><option value={method.id} key={method.id}>{method.name} · rate {method.exchange_rate}</option>)}</select></td>
                  <td><input type="number" step="0.01" min="0" value={line.gross_tender_amount} onChange={(e)=>updateTender(index,"gross_tender_amount",e.target.value)}/></td>
                  <td><input value={line.reference} onChange={(e)=>updateTender(index,"reference",e.target.value.toUpperCase())}/></td>
                  <td>{result?.handling_fee_amount || "—"}</td>
                  <td>{result?.exchange_rate || "—"}</td>
                  <td>{result?.home_currency_amount || "—"}</td>
                  <td><button className="text" onClick={()=>setTenders(tenders.filter((_,row)=>row!==index))}>Remove</button></td>
                </tr>
              })}
            </tbody>
          </table>
        </div>
        <div className="payment-panel-actions">
          <label>Overall reference<input value={reference} onChange={(e)=>setReference(e.target.value.toUpperCase())}/></label>
          <button onClick={calculate}>Calculate conversion</button>
          <strong>Home-currency total: {quote?.total_home_amount || "0.00"}</strong>
        </div>
      </section>

      <section className="payment-panel">
        <header>
          <div><span>2</span><h2>Tag payment against customer bills</h2></div>
          <button className="secondary" disabled={!quote} onClick={autoAllocate}>Allocate oldest first</button>
        </header>
        <div className="bill-table">
          <table>
            <thead><tr><th>Bill</th><th>Date</th><th>Type</th><th>Bill amount</th><th>Outstanding</th><th>Allocate</th><th>After payment</th></tr></thead>
            <tbody>
              {data.bills.map((bill)=>{
                const amount=Number(allocations[bill.id]||0);
                return <tr key={bill.id}>
                  <td>{bill.code}</td><td>{bill.bill_date}</td><td>{bill.bill_type}</td><td>{bill.bill_amount}</td><td>{bill.balance}</td>
                  <td><input type="number" min="0" max={bill.balance} step="0.01" value={allocations[bill.id]||""} onChange={(e)=>setAllocations({...allocations,[bill.id]:e.target.value})}/></td>
                  <td>{Math.max(Number(bill.balance)-amount,0).toFixed(2)}</td>
                </tr>
              })}
            </tbody>
          </table>
        </div>
        <div className="allocation-totals">
          <span>Converted payment <strong>{quote?.total_home_amount || "0.00"}</strong></span>
          <span>Allocated to bills <strong>{allocationTotal.toFixed(2)}</strong></span>
          <span>Policy credit <strong>{Math.max(Number(quote?.total_home_amount||0)-allocationTotal,0).toFixed(2)}</strong></span>
        </div>
      </section>

      <footer className="payment-footer">
        <p>
          Posting creates the receipt, tender conversion snapshots, bill
          allocations, policy balance, commission and status reassessment in
          one transaction.
        </p>
        <button disabled={saving || !quote || allocationTotal <= 0} onClick={post}>
          {saving ? "Posting payment…" : "Post allocated payment"}
        </button>
      </footer>
    </section>
  );
}

export default PolicyPaymentWorkspace;
